/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Self,
} from '@angular/core';
import { AppRouter, ContainerStateDirective } from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { CONTEXTABLE_COMPONENT, ContextableComponent, ContextRouteAttachment } from '../../routing/context';

@Directive({
  selector: '[ghFormMenuOption]',
})
export class FormMenuOptionDirective {
}

@Directive({
  selector: 'gh-form-toolbar-options[cancel]', // tslint:disable-line:directive-selector
})
export class FormToolbarOptionsCancelDirective {
  @Output() cancel = new EventEmitter<void>();
}

@Component({
  selector: 'gh-form-toolbar-options',
  template: require('./form-toolbar-options.component.html'),
})
export class FormToolbarOptionsComponent implements OnInit, OnDestroy {
  @Input() parentRoute: string;

  // condition used to enable main form buttons. By default main form buttons are enabled when there is any change
  @Input() enabledWhen: 'always' | 'dirty' = 'dirty';
  @Input() hasResetButton: boolean = false;
  @Input() hasProcessButton: boolean = true;
  @Input() hasCancelButton: boolean = true;
  @Input() hasAelButton: boolean = true;
  @Input() processEnabled: boolean;
  @Input() resetEnabled: boolean;

  @Output() process = new EventEmitter<any>();
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() reset = new EventEmitter<any>();
  @Output() ael = new EventEmitter<any>();

  @ContentChildren(FormMenuOptionDirective) fileButtons: QueryList<any>;

  private changesSubscription?: Subscription;

  get displayWorfklowButtons(): boolean {
    const { mode } = this.containerState.containerState;
    return [FormMode.EditMode, FormMode.CreateMode].includes(mode)
      && (this.hasProcessButton || this.hasResetButton || this.hasCancelButton);
  }

  get isWorkflowButtonDisabled(): boolean {
    const { containerState } = this.containerState;
    if (containerState.editMode) {
      switch (this.enabledWhen) {
        case 'dirty':
          return !containerState.dirty;
        case 'always':
          return false;
        default:
          return false;
      }
    } else {
      return false;
    }
  }

  get isProcessButtonDisabled(): boolean {
    return isNotNil(this.processEnabled) ? !this.processEnabled : this.isWorkflowButtonDisabled;
  }

  get isResetButtonDisabled(): boolean {
    return isNotNil(this.resetEnabled) ? !this.resetEnabled : this.isWorkflowButtonDisabled;
  }

  get displayAelButton(): boolean {
    // display AEL button only if
    // 1. we are in create mode
    return !this.containerState.containerState.createMode
      // 2. AEL was allowed
      && this.hasAelButton
      // 3. we are not in the contextable component or we are in the context of Angular router
      // (there is no sense to display AEL in the context of dialog,
      // because in this case we cannot route to the AEL page)
      && (isNil(this.contextable) || this.contextable.attachment === ContextRouteAttachment.Router);
  }

  get displayWorkflowDivider(): boolean {
    return this.displayWorfklowButtons && (this.displayAelButton || this.displayFileMenu);
  }

  get displayFileDivider(): boolean {
    return this.displayFileMenu && this.displayAelButton;
  }

  get displayFileMenu(): boolean {
    return this.fileButtons.length > 0;
  }

  constructor(private changeDetectorRef: ChangeDetectorRef,
              @Optional() private containerState: ContainerStateDirective,
              private router: AppRouter,
              @Optional() @Self() private cancelHandler?: FormToolbarOptionsCancelDirective,
              @Optional() @Inject(CONTEXTABLE_COMPONENT) private contextable?: ContextableComponent) {
    if (isNil(containerState)) {
      throw new Error(`'gh-form-toolbar-options' cannot find 'ghContainerState' directive`);
    }
  }

  ngOnInit(): void {
    const { changes } = this.containerState.containerState;

    if (changes) {
      this.changesSubscription = changes.subscribe(() => this.changeDetectorRef.markForCheck());
    }
  }

  ngOnDestroy(): void {
    const { changesSubscription } = this;
    if (changesSubscription) {
      changesSubscription.unsubscribe();
    }
  }

  onCancel(): void {
    if (this.cancelHandler) {
      this.cancelHandler.cancel.emit();
    } else if (this.parentRoute) {
      this.router.navigateByUrl(this.parentRoute);
    } else if (this.contextable) {
      this.contextable.cancel();
    } else {
      this.router.navigateBack();
    }
  }
}
